import React, { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { getApiBaseUrl, getImageUrl } from '../utils/api';
import { formatPrice } from '../utils/currency';
import { getColourHex, getColourLabel } from '../utils/dressColour';
import { getCareSymbolItems } from '../utils/careSymbols';
import { translate } from '../utils/translate';
import './PublicCatalogue.css';
import './PublicDressPage.css';

const RESERVED_SLUGS = new Set([
  'login', 'register', 'forgot-password', 'reset-password', 'verify-email',
  'dashboard', 'create-salon', 'salon', 'privacy-policy', 'terms-and-conditions',
  'pricing', 'api', 'uploads'
]);

const PRODUCT_DETAIL_FIELDS = [
  ['item_code', 'itemCode', 'Item code'],
  ['country_of_origin', 'countryOfOrigin', 'Country of origin'],
  ['manufacturer', 'manufacturer', 'Manufacturer'],
  ['importer', 'importer', 'Importer'],
  ['maintenance_procedure', 'maintenanceProcedure', 'Maintenance procedure'],
  ['material_composition', 'materialComposition', 'Material composition'],
  ['shrinkage_during_care', 'shrinkageDuringCare', 'Shrinkage during care']
];

function PublicDressPage() {
  const { salonSlug, dressId } = useParams();
  const [state, setState] = useState({ status: 'loading', salon: null, dress: null });

  useEffect(() => {
    const slug = (salonSlug || '').toLowerCase().trim();
    if (!slug || RESERVED_SLUGS.has(slug) || !dressId) {
      setState({ status: 'notfound', salon: null, dress: null });
      return;
    }

    const base = getApiBaseUrl();
    fetch(`${base}/api/public/catalogue/${encodeURIComponent(slug)}/dress/${encodeURIComponent(dressId)}`)
      .then((res) => {
        if (res.status === 404) {
          setState({ status: 'notfound', salon: null, dress: null });
          return null;
        }
        if (!res.ok) throw new Error('Failed to load dress');
        return res.json();
      })
      .then((data) => {
        if (data) {
          setState({ status: 'ok', salon: data.salon, dress: data.dress });
        }
      })
      .catch(() => {
        setState({ status: 'error', salon: null, dress: null });
      });
  }, [salonSlug, dressId]);

  const slug = (salonSlug || '').toLowerCase().trim();
  if (!slug || RESERVED_SLUGS.has(slug)) {
    return <Navigate to="/" replace />;
  }

  if (state.status === 'loading') {
    return (
      <div className="public-catalogue">
        <div className="public-catalogue-loading">Loading...</div>
      </div>
    );
  }

  if (state.status === 'notfound') {
    return (
      <div className="public-catalogue">
        <div className="public-catalogue-notfound">Dress not found.</div>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="public-catalogue">
        <div className="public-catalogue-error">Something went wrong. Please try again later.</div>
      </div>
    );
  }

  const { salon, dress } = state;
  const currency = salon.currency || 'EUR';
  const conversionRate = salon.conversionRate || 1;
  const t = (key, defaultValue, params) => translate(salon.language || 'en', key, defaultValue, params);
  const convert = (amount) => (amount == null ? amount : Number(amount) * conversionRate);

  const productDetails = PRODUCT_DETAIL_FIELDS
    .map(([field, i18nKey, defaultLabel]) => ({
      field,
      label: t(`dresses.${i18nKey}`, defaultLabel),
      value: dress[field]
    }))
    .filter((item) => item.value);

  const careItems = getCareSymbolItems(dress, { t });

  const imagePath = dress.image_small || (dress.images && dress.images[0]);

  return (
    <div className="public-catalogue">
      <header className="public-catalogue-header">
        <h1 className="public-catalogue-title">{salon.name}</h1>
      </header>

      <main className="public-dress-page-main">
        <div className="public-dress-page-summary">
          <div className="public-dress-page-icon">
            {imagePath ? (
              <img src={getImageUrl(imagePath)} alt={dress.name} />
            ) : (
              <div className="public-dress-page-icon-placeholder">
                {t('dresses.publicPageNoImage', 'No image')}
              </div>
            )}
          </div>
          <div className="public-dress-page-facts">
            <h2 className="public-dress-page-name">
              {t('dresses.publicPageTitle', '{name} Wedding Dress', { name: dress.name })}
            </h2>
            <div className="public-dress-page-fact-grid">
              {dress.rental_price != null && Number(dress.rental_price) > 0 && (
                <div className="public-dress-page-fact">
                  <span className="public-dress-page-fact-label">{t('dresses.rentalPrice', 'Rental price')}</span>
                  <span>{formatPrice(convert(dress.rental_price), currency)}</span>
                </div>
              )}
              {dress.selling_price != null && Number(dress.selling_price) > 0 && (
                <div className="public-dress-page-fact">
                  <span className="public-dress-page-fact-label">{t('dresses.sellingPrice', 'Selling price')}</span>
                  <span>{formatPrice(convert(dress.selling_price), currency)}</span>
                </div>
              )}
              <div className="public-dress-page-fact">
                <span className="public-dress-page-fact-label">{t('dresses.size', 'Size')}</span>
                <span>{dress.size || '-'}</span>
              </div>
              <div className="public-dress-page-fact">
                <span className="public-dress-page-fact-label">{t('dresses.color', 'Colour')}</span>
                {dress.colour ? (
                  <span className="public-dress-page-colour">
                    <span
                      className="public-dress-page-colour-swatch"
                      style={{ backgroundColor: getColourHex(dress.colour) }}
                    ></span>
                    {getColourLabel(t, dress.colour)}
                  </span>
                ) : (
                  <span>-</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {productDetails.length > 0 && (
          <div className="public-dress-page-section">
            <h3 className="public-dress-page-section-title">{t('dresses.productDetails', 'Product details')}</h3>
            <div className="public-dress-page-details-grid">
              {productDetails.map(({ field, label, value }) => (
                <div className="public-dress-page-details-row" key={field}>
                  <span className="public-dress-page-details-label">{label}</span>
                  <span className="public-dress-page-details-value">{value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {careItems.length > 0 && (
          <div className="public-dress-page-section">
            <h3 className="public-dress-page-section-title">{t('dresses.careInstructions', 'Care instructions')}</h3>
            <div className="public-dress-page-care-grid">
              {careItems.map((item) => (
                <div className="public-dress-page-care-item" key={item.field} title={item.label}>
                  <img src={item.iconUrl} alt={item.label} />
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      <footer className="public-catalogue-footer">
        <a href={`/shops/${salon.slug}`}>{salon.name}</a> · {t('dresses.publicPagePoweredBy', 'Powered by')} <a href="/">Dress Desk</a>
      </footer>
    </div>
  );
}

export default PublicDressPage;
